import React, { useEffect, useMemo, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from "recharts";
import { X, Layers as LayersIcon } from "lucide-react";
import {
    fetchSgStrategyModuleStats, fetchSgStrategyModuleEquity,
    fetchSgStrategyModuleTrades, fetchSgStrategyModuleInputs,
    setSgStrategyModuleInput,
} from "@/lib/smartgrid_api";

const C = {
    bg: "#0B0F14", panel: "#0F1620", panel2: "#121C28", border: "#1B2A3A",
    text: "#E6EDF6", sub: "#A7B4C6", dim: "#6F7D92",
    bull: "#2BE38B", bear: "#FF5C6C", cyan: "#2EE6D6", blue: "#3DA7FF",
    warn: "#FFB020",
};

const LAYER_TITLES = { 0: "Layer 0 · Data", 1: "Layer 1 · Prediction", 2: "Layer 2 · Controls" };

const fmt = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v))
    ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));
const money = (v) => (v === null || v === undefined ? "—"
    : `${Number(v) >= 0 ? "+" : ""}$${Number(v).toFixed(2)}`);
const pnlColor = (v) => (v === null || v === undefined ? C.dim
    : Number(v) >= 0 ? C.bull : C.bear);
const ms2h = (ms) => (ms === null || ms === undefined ? "—"
    : ms >= 3_600_000 ? `${(ms / 3_600_000).toFixed(1)}h`
        : ms >= 60_000 ? `${(ms / 60_000).toFixed(1)}m` : `${Math.round(ms / 1000)}s`);

const Tile = ({ label, value, color, testid }) => (
    <div className="rounded-lg px-3 py-2" style={{ background: C.panel2, border: `1px solid ${C.border}` }}
        data-testid={testid}>
        <div className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>{label}</div>
        <div className="font-mono text-[15px] tabular-nums" style={{ color: color || C.text }}>{value}</div>
    </div>
);

const InputToggle = ({ name, on, onFlip, sid }) => (
    <button
        onClick={onFlip}
        className="btn-anim flex items-center justify-between gap-2 px-2.5 h-8 rounded-md text-[11px] w-full"
        style={{
            color: on ? C.text : C.dim, background: on ? C.panel2 : "transparent",
            border: `1px solid ${on ? C.border : "#141d29"}`,
        }}
        data-testid={`strategy-input-toggle-${sid}-${name}`}
        title={on ? "input enabled — click to disable for this strategy" : "input disabled for this strategy"}
    >
        <span className="truncate">{name}</span>
        <span className="font-mono text-[10px]" style={{ color: on ? C.bull : C.dim }}>
            {on ? "ON" : "OFF"}
        </span>
    </button>
);

const BucketTable = ({ title, rows, testid }) => (
    <div className="rounded-lg p-3" style={{ background: C.panel2, border: `1px solid ${C.border}` }}
        data-testid={testid}>
        <div className="text-[10px] uppercase tracking-[0.12em] mb-2" style={{ color: C.dim }}>{title}</div>
        {(!rows || rows.length === 0) ? (
            <div className="text-[11px]" style={{ color: C.dim }}>no closed cycles yet</div>
        ) : (
            <table className="w-full text-[11px]">
                <tbody>
                    {rows.slice(0, 6).map((r) => (
                        <tr key={r.bucket}>
                            <td className="py-0.5" style={{ color: C.sub }}>{r.bucket}</td>
                            <td className="py-0.5 text-right font-mono tabular-nums" style={{ color: C.dim }}>{r.cycles}×</td>
                            <td className="py-0.5 text-right font-mono tabular-nums" style={{ color: C.sub }}>{fmt(r.win_rate_pct, 0)}%</td>
                            <td className="py-0.5 text-right font-mono tabular-nums" style={{ color: pnlColor(r.pnl) }}>{money(r.pnl)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

/**
 * Per-strategy drill-down dashboard: full StatBlock, equity curve, context
 * buckets, per-layer input toggles, attributed trades.
 */
export const StrategyDashboard = ({ sid, meta, onClose, onToggleEnabled }) => {
    const [stats, setStats] = useState(null);
    const [equity, setEquity] = useState([]);
    const [trades, setTrades] = useState([]);
    const [inputs, setInputs] = useState(null);
    const [err, setErr] = useState("");

    const load = async () => {
        try {
            const [st, eq, tr, inp] = await Promise.all([
                fetchSgStrategyModuleStats(sid),
                fetchSgStrategyModuleEquity(sid),
                fetchSgStrategyModuleTrades(sid, 40),
                fetchSgStrategyModuleInputs(sid),
            ]);
            setStats(st); setEquity(eq.series || []); setTrades(tr.items || []);
            setInputs(inp); setErr("");
        } catch (e) {
            setErr(String(e?.response?.data?.detail || e.message || e));
        }
    };
    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sid]);

    const s = stats?.stats || null;
    const color = stats?.color || meta?.color || C.cyan;
    const eqData = useMemo(() => equity.map(([ts, v]) => ({
        t: new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        v: Number(v),
    })), [equity]);

    const flipInput = async (name, on) => {
        try {
            const res = await setSgStrategyModuleInput(sid, name, !on);
            setInputs((prev) => ({ ...(prev || {}), inputs: res.inputs }));
        } catch (e) { setErr(String(e?.response?.data?.detail || e.message)); }
    };

    const flipLayer = async (layer, group) => {
        const names = Object.keys(group || {});
        if (!names.length) return;
        const allOn = names.every((name) => group[name]);
        const next = !allOn;
        if (!next && !window.confirm(
            `Skip all advisory Layer ${layer} inputs for ${sid}? Absolute safety vetoes remain active.`
        )) return;
        try {
            await Promise.all(names.map((name) =>
                setSgStrategyModuleInput(sid, name, next)));
            setInputs(await fetchSgStrategyModuleInputs(sid));
        } catch (e) { setErr(String(e?.response?.data?.detail || e.message)); }
    };

    return (
        <div className="panel p-4 mt-4" data-testid={`strategy-dashboard-${sid}`}>
            <div className="flex items-center gap-3 mb-3">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: color }} />
                <div className="text-[15px] font-medium" style={{ color: C.text }}>
                    {stats?.name || meta?.name || sid}
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.12em]"
                    style={{ color: C.dim, border: `1px solid ${C.border}` }}>
                    {stats?.status || meta?.status || "—"}
                </span>
                {onToggleEnabled && !meta?.locked && (
                    <button
                        onClick={() => onToggleEnabled(sid, !(stats?.enabled ?? meta?.enabled))}
                        className="btn-anim px-2.5 h-7 rounded-md text-[11px]"
                        style={{
                            color: (stats?.enabled ?? meta?.enabled) ? C.bull : C.dim,
                            border: `1px solid ${C.border}`, background: C.panel2,
                        }}
                        data-testid={`strategy-dashboard-toggle-${sid}`}
                    >
                        {(stats?.enabled ?? meta?.enabled) ? "ENABLED" : "DISABLED"}
                    </button>
                )}
                {meta?.locked && (
                    <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>
                        locked · base ladder
                    </span>
                )}
                <div className="flex-1" />
                {onClose && (
                    <button onClick={onClose} className="btn-anim p-1.5 rounded-md"
                        style={{ color: C.dim, border: `1px solid ${C.border}` }}
                        data-testid={`strategy-dashboard-close-${sid}`}>
                        <X size={14} />
                    </button>
                )}
            </div>
            {err && <div className="text-[11px] mb-2" style={{ color: C.bear }}>{err}</div>}
            {(stats?.blocked_reason || meta?.blocked_reason) && (
                <div className="rounded-md px-3 py-2 mb-3 text-[11px]"
                    style={{ color: C.sub, background: C.panel2, border: `1px solid ${C.border}` }}
                    data-testid={`sd-${sid}-blocked-reason`}>
                    <span className="uppercase tracking-[0.12em] text-[9px] mr-2" style={{ color: C.dim }}>Current gate</span>
                    {stats?.blocked_reason || meta?.blocked_reason}
                </div>
            )}
            {stats?.last_error && (
                <div className="rounded-md px-3 py-2 mb-3 text-[11px]"
                    style={{ color: C.bear, background: "rgba(255,92,108,0.06)", border: "1px solid rgba(255,92,108,0.3)" }}
                    title={`${stats.last_error.site || ""} (x${stats.last_error.count || 1})`}
                    data-testid={`sd-${sid}-last-error`}>
                    <span className="uppercase tracking-[0.12em] text-[9px] mr-2" style={{ color: C.dim }}>Last contained error</span>
                    {stats.last_error.error}
                </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
                <Tile label="Total PnL" value={money(s?.total_pnl)} color={pnlColor(s?.total_pnl)} testid={`sd-${sid}-total-pnl`} />
                <Tile label="Realized" value={money(s?.realized_pnl)} color={pnlColor(s?.realized_pnl)} testid={`sd-${sid}-realized`} />
                <Tile label="Unrealized" value={money(s?.unrealized_pnl)} color={pnlColor(s?.unrealized_pnl)} testid={`sd-${sid}-unrealized`} />
                <Tile label="Fees" value={money(s?.fees_usdt && -Math.abs(s.fees_usdt))} color={C.warn} testid={`sd-${sid}-fees`} />
                <Tile label="Net PnL" value={money(s?.net_pnl)} color={pnlColor(s?.net_pnl)} testid={`sd-${sid}-net`} />
                <Tile label="Win rate" value={`${fmt(s?.win_rate_pct, 1)}%`} testid={`sd-${sid}-winrate`} />
                <Tile label="Profit factor" value={fmt(s?.profit_factor)} testid={`sd-${sid}-pf`} />
                <Tile label="Sharpe" value={fmt(s?.sharpe, 3)} testid={`sd-${sid}-sharpe`} />
                <Tile label="Sortino" value={fmt(s?.sortino, 3)} testid={`sd-${sid}-sortino`} />
                <Tile label="Max DD" value={`${fmt(s?.max_drawdown_pct)}%`} color={s?.max_drawdown_pct < 0 ? C.bear : C.text} testid={`sd-${sid}-maxdd`} />
                <Tile label="Cycles" value={fmt(s?.cycles, 0)} testid={`sd-${sid}-cycles`} />
                <Tile label="Avg hold" value={ms2h(s?.avg_hold_ms)} testid={`sd-${sid}-hold`} />
                <Tile label="Avg grid width" value={fmt(s?.avg_grid_width)} testid={`sd-${sid}-width`} />
                <Tile label="Capital in use" value={`$${fmt(s?.capital_in_use_usdt, 0)}`} testid={`sd-${sid}-capital`} />
            </div>

            <div className="grid lg:grid-cols-2 gap-3 mb-3">
                <div className="rounded-lg p-3" style={{ background: C.panel2, border: `1px solid ${C.border}` }}>
                    <div className="text-[10px] uppercase tracking-[0.12em] mb-1" style={{ color: C.dim }}>
                        Equity (realized + unrealized)
                    </div>
                    {eqData.length < 2 ? (
                        <div className="text-[11px] py-6 text-center" style={{ color: C.dim }}>
                            equity series builds while the bot runs
                        </div>
                    ) : (
                        <div style={{ height: 120 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={eqData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                                    <defs>
                                        <linearGradient id={`eq-${sid}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                                            <stop offset="100%" stopColor={color} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <XAxis dataKey="t" tick={{ fill: C.dim, fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={40} />
                                    <YAxis tick={{ fill: C.dim, fontSize: 10 }} tickLine={false} axisLine={false} width={46}
                                        tickFormatter={(v) => `$${v.toFixed(0)}`} domain={["auto", "auto"]} />
                                    <RTooltip contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11 }}
                                        labelStyle={{ color: C.dim }} itemStyle={{ color }} formatter={(v) => [`$${Number(v).toFixed(2)}`, "equity"]} />
                                    <Area type="monotone" dataKey="v" stroke={color} strokeWidth={1.5}
                                        fill={`url(#eq-${sid})`} isAnimationActive={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 mt-2">
                        <Tile label="Prediction accuracy" value={s?.prediction?.accuracy_pct === null || s?.prediction?.accuracy_pct === undefined ? "—" : `${fmt(s.prediction.accuracy_pct, 1)}%`} testid={`sd-${sid}-pred-acc`} />
                        <Tile label="Expected vs actual (TP hit)" value={s?.expected_vs_actual?.tp_hit_rate_pct === null || s?.expected_vs_actual?.tp_hit_rate_pct === undefined ? "—" : `${fmt(s.expected_vs_actual.tp_hit_rate_pct, 1)}%`} testid={`sd-${sid}-tp-hit`} />
                    </div>
                </div>

                <div className="rounded-lg p-3" style={{ background: C.panel2, border: `1px solid ${C.border}` }}
                    data-testid={`sd-${sid}-inputs`}>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] mb-2" style={{ color: C.dim }}>
                        <LayersIcon size={11} /> Layer inputs (per-strategy toggles)
                    </div>
                    {!inputs ? (
                        <div className="text-[11px]" style={{ color: C.dim }}>loading…</div>
                    ) : (
                        [0, 1, 2].map((layer) => {
                            const group = inputs.inputs?.[String(layer)] || {};
                            const names = Object.keys(group);
                            if (!names.length) return null;
                            return (
                                <div key={layer} className="mb-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-[10px]" style={{ color: C.dim }}>{LAYER_TITLES[layer]}</div>
                                        <button onClick={() => flipLayer(layer, group)}
                                            className="btn-anim px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.1em]"
                                            style={{ color: C.sub, border: `1px solid ${C.border}` }}
                                            data-testid={`strategy-layer-toggle-${sid}-${layer}`}>
                                            {names.every((n) => group[n]) ? "Skip layer" : "Use layer"}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {names.map((n) => (
                                            <InputToggle key={n} name={n} on={group[n]} sid={sid}
                                                onFlip={() => flipInput(n, group[n])} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div className="text-[10px] mt-1" style={{ color: C.dim }}>
                        Safety vetoes (kill switch · pause · breaker) are global and never maskable.
                    </div>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                <BucketTable title="By regime" rows={s?.by_regime} testid={`sd-${sid}-by-regime`} />
                <BucketTable title="By volatility" rows={s?.by_volatility} testid={`sd-${sid}-by-vol`} />
                <BucketTable title="By market trend" rows={s?.by_trend} testid={`sd-${sid}-by-trend`} />
                <BucketTable title="By hour (UTC)" rows={s?.by_hour} testid={`sd-${sid}-by-hour`} />
                <BucketTable title="By day of week" rows={s?.by_day_of_week} testid={`sd-${sid}-by-dow`} />
                <BucketTable title="By exit kind" rows={s?.by_exit_kind} testid={`sd-${sid}-by-exit`} />
            </div>

            <div className="grid lg:grid-cols-2 gap-3">
                <div className="rounded-lg p-3" style={{ background: C.panel2, border: `1px solid ${C.border}` }}
                    data-testid={`sd-${sid}-activations`}>
                    <div className="text-[10px] uppercase tracking-[0.12em] mb-2" style={{ color: C.dim }}>Control activations</div>
                    {(!s?.control_activations || Object.keys(s.control_activations).length === 0) ? (
                        <div className="text-[11px]" style={{ color: C.dim }}>none recorded in the action window</div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(s.control_activations).map(([k, v]) => (
                                <span key={k} className="px-2 py-1 rounded text-[10px] font-mono"
                                    style={{ color: C.sub, border: `1px solid ${C.border}` }}>
                                    {k} ×{v}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="rounded-lg p-3 overflow-x-auto" style={{ background: C.panel2, border: `1px solid ${C.border}` }}
                    data-testid={`sd-${sid}-trades`}>
                    <div className="text-[10px] uppercase tracking-[0.12em] mb-2" style={{ color: C.dim }}>
                        Attributed trades (latest {trades.length})
                    </div>
                    {trades.length === 0 ? (
                        <div className="text-[11px]" style={{ color: C.dim }}>no trades attributed to this strategy yet</div>
                    ) : (
                        <table className="w-full text-[11px]">
                            <thead>
                                <tr style={{ color: C.dim }}>
                                    <th className="text-left font-normal">side</th>
                                    <th className="text-right font-normal">price</th>
                                    <th className="text-right font-normal">qty</th>
                                    <th className="text-right font-normal">pnl</th>
                                    <th className="text-right font-normal">regime</th>
                                </tr>
                            </thead>
                            <tbody className="font-mono tabular-nums">
                                {trades.slice(0, 12).map((t, i) => (
                                    <tr key={`${t.client_order_id || i}-${t.filled_at_ms}`}>
                                        <td style={{ color: t.side === "BUY" ? C.bull : C.bear }}>{t.side === "BUY" ? "▲ BUY" : "▼ SELL"}</td>
                                        <td className="text-right" style={{ color: C.sub }}>{fmt(t.price)}</td>
                                        <td className="text-right" style={{ color: C.dim }}>{t.qty}</td>
                                        <td className="text-right" style={{ color: pnlColor(t.realized_pnl) }}>{money(t.realized_pnl)}</td>
                                        <td className="text-right" style={{ color: C.dim }}>{t.regime || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};
